/**
 * React Query hook for organization data
 *
 * Provides data fetching for a single organization with automatic caching.
 */

import { useQuery } from '@tanstack/react-query';
import { OrganizationApiService } from '../services/organizationApi';
import type { OrganizationWithCounts } from '../types/organization';

/**
 * Query key factory for organization queries
 */
export const organizationKeys = {
  all: ['organization'] as const,
  detail: (orgId: number) => ['organization', orgId] as const,
  tenants: (orgId: number) => ['organization', orgId, 'tenants'] as const,
  pendingRequests: (orgId: number) => ['organization', orgId, 'pendingRequests'] as const,
};

/**
 * Hook return type for useOrganization
 */
export interface UseOrganizationResult {
  /** Organization data */
  organization: OrganizationWithCounts | undefined;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Error from fetching organization */
  error: Error | null;
  /** Refetch organization data */
  refetch: () => void;
}

/**
 * React Query hook for fetching organization data
 *
 * @param orgId - Organization ID to fetch, or undefined to skip fetching
 * @returns Organization data, loading states, and refetch function
 *
 * @example
 * ```tsx
 * const { organization, isLoading, error, refetch } = useOrganization(1);
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 *
 * return <h1>{organization.name}</h1>;
 * ```
 */
export function useOrganization(orgId: number | undefined): UseOrganizationResult {
  const {
    data: organization,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: organizationKeys.detail(orgId!),
    queryFn: () => OrganizationApiService.getOrganization(orgId!),
    enabled: orgId !== undefined,
  });

  return {
    organization,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}
